import React, { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { STUDY_HUB } from '../../config/appLabels';
import './LearningGuide.css';

const TITLES = {
  hub: `How ${STUDY_HUB.name} works`,
  library: 'About the library',
  questions: 'About the library',
  saved: 'About Saved',
  practice: 'About Practice',
  review: 'About Review',
  analytics: 'About Analytics',
};

export default function LearningGuide({ focus = 'hub' }) {
  const detailsId = useId();
  const [open, setOpen] = useState(false);
  const title = TITLES[focus] || TITLES.hub;

  return (
    <section className="learning-guide" aria-labelledby={detailsId}>
      <button
        type="button"
        className="learning-guide-toggle"
        aria-expanded={open}
        aria-controls={`${detailsId}-body`}
        onClick={() => setOpen((v) => !v)}
      >
        <span id={detailsId}>{title}</span>
        <span className="learning-guide-chevron" aria-hidden>{open ? '−' : '+'}</span>
      </button>

      {open && (
        <div id={`${detailsId}-body`} className="learning-guide-body">
          {focus === 'hub' && <HubGuide />}
          {(focus === 'library' || focus === 'questions') && <LibraryGuide />}
          {focus === 'saved' && <SavedGuide />}
          {focus === 'practice' && <PracticeGuide />}
          {focus === 'review' && <ReviewGuide />}
          {focus === 'analytics' && <AnalyticsGuide />}
        </div>
      )}
    </section>
  );
}

function HubGuide() {
  return (
    <>
      <p className="learning-guide-lead">
        <strong>{STUDY_HUB.name}</strong> is your command center: live stats, due reviews, recommendations, and shortcuts
        to Practice and Review.
      </p>
      <ul className="learning-guide-list">
        <li><strong>Stats</strong> — read, saved, due today, streak, and library size.</li>
        <li><strong>Progress &amp; analytics</strong> — 14-day charts, topic focus, and review schedule health.</li>
        <li><strong>Daily reviews</strong> — progress toward the goal you set in Settings.</li>
        <li><strong>Revision due</strong> — same queue as the Review page (spaced repetition).</li>
        <li><strong>Flow</strong> — browse in the <Link to="/library">Library</Link>, save important items, then Practice and Review.</li>
      </ul>
    </>
  );
}

function LibraryGuide() {
  return (
    <>
      <p className="learning-guide-lead">
        The <strong>Library</strong> is where you search and read Q&amp;A. Reading builds familiarity; retention comes from
        save → practice → review.
      </p>
      <ul className="learning-guide-list">
        <li>Use filters and search to find topics for your goals.</li>
        <li><strong>Save</strong> questions you want in your deck — that adds them to Saved and starts a review schedule.</li>
        <li>Track overall progress on <Link to={STUDY_HUB.path}>{STUDY_HUB.name}</Link>, not only here.</li>
      </ul>
    </>
  );
}

function SavedGuide() {
  return (
    <>
      <p className="learning-guide-lead">
        <strong>Saved</strong> is your personal deck: bookmarks for Practice and the spaced-repetition system.
      </p>
      <ul className="learning-guide-list">
        <li>Saving creates a review entry; items can appear on <Link to="/review">Review</Link> when due.</li>
        <li>Start a session with <Link to="/practice">Practice</Link> using your saved deck (or library mode in Settings).</li>
        <li>Removing a save does not erase read history, but it removes the item from your saved deck.</li>
      </ul>
    </>
  );
}

function PracticeGuide() {
  return (
    <>
      <p className="learning-guide-lead">
        <strong>Practice</strong> is active study: reveal the answer, then grade yourself. Each grade updates when that
        question returns on Review.
      </p>
      <ul className="learning-guide-list">
        <li>Deck source: saved questions or a library sample — change in <Link to="/settings">Settings</Link>.</li>
        <li>Grading uses <strong>Again · Hard · Good · Easy</strong> (see buttons under the answer).</li>
        <li>Practice anytime; Review only shows items that are <em>due now</em>.</li>
      </ul>
      <GradeLegend />
      <p className="learning-guide-tip">
        After a round, check <Link to="/review">Review</Link> for due items.
      </p>
    </>
  );
}

function ReviewGuide() {
  return (
    <>
      <p className="learning-guide-lead">
        <strong>Review</strong> is your due queue — questions you have saved or practiced, scheduled to come back when
        their interval ends.
      </p>
      <ul className="learning-guide-list">
        <li>Only items with <strong>next review ≤ now</strong> appear here.</li>
        <li>Nothing due? Save from the <Link to="/library">Library</Link>, practice once, or wait for the schedule.</li>
        <li>Browsing alone does not add items — save or practice first.</li>
      </ul>
      <GradeLegend />
      <p className="learning-guide-tip">
        Finish due items here first, then use Practice for extra study.
      </p>
    </>
  );
}

function AnalyticsGuide() {
  return (
    <>
      <p className="learning-guide-lead">
        <strong>Analytics</strong> summarizes how you engage with the library and your review schedule over the last two weeks.
      </p>
      <ul className="learning-guide-list">
        <li><strong>Activity</strong> — daily reads and review grades (Practice and Review both count).</li>
        <li><strong>Topics</strong> — where you spend the most read time.</li>
        <li><strong>Schedule health</strong> — how many cards are scheduled, due, learning, or mature intervals.</li>
        <li>Improve retention by keeping Review clear and saving questions you care about.</li>
      </ul>
    </>
  );
}

function GradeLegend() {
  return (
    <dl className="learning-guide-grades">
      <div><dt>Again</dt><dd>Forgot — review again soon.</dd></div>
      <div><dt>Hard</dt><dd>Struggled — shorter interval.</dd></div>
      <div><dt>Good</dt><dd>Knew it — normal interval increase.</dd></div>
      <div><dt>Easy</dt><dd>Too easy — longer interval.</dd></div>
    </dl>
  );
}
